import { ProposalUpdatePaths, useLazyProposalUpdate } from 'modules/proposals';
import { Unit, unitSelector, useUnits } from 'modules/units';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from 'shared/components';
import {
  useCloseOnEscape,
  useMobile,
  useModalGuard,
  useSubmitOnEnter,
} from 'shared/hooks';
import { UnitNameInput } from './fragments';

interface Props extends ModalProps {
  inputValue?: string;
  valueToUpdate?: ProposalUpdatePaths;
  index?: number;
  item?: Unit;
  onCreate?: (unit: Unit) => void;
}

export const UnitModal: React.FC<Props> = ({
  inputValue,
  valueToUpdate,
  index,
  item,
  onCreate,
  onClose,
  ...rest
}) => {
  const isMobile = useMobile();
  const closeButtonRef = useCloseOnEscape<HTMLButtonElement>();
  const submitButtonRef = useSubmitOnEnter<HTMLButtonElement>();

  const [nameInitialValue, setNameInitialValue] = useState('');

  const form = useForm<Unit>({
    mode: 'onSubmit',
    defaultValues: new Unit(item),
  });
  const context = useFormContext<Proposal>();
  const { setFormItem } = useLazyProposalUpdate<Unit>({
    valueToUpdate,
    selector: unitSelector,
    valuesToCompare: ['name'],
    callback: onClose,
  });

  const { handleClose } = useModalGuard();
  const { createEntityAsync, updateEntityAsync } = useUnits();

  async function handleUpdate(unit: Unit) {
    const { id, ...rest } = unit;
    if (id) await updateEntityAsync(id, rest);
  }

  async function handleCreate(unit: Unit) {
    const createdUnitPayload = {
      ...unit,
      index: -1,
    };
    const id = await createEntityAsync(createdUnitPayload);
    if (!id) return;

    const createdUnit = {
      ...createdUnitPayload,
      id,
    };

    if (context && index !== undefined) {
      context.setValue(
        `segments.${index}.unit`,
        createdUnit,
        {
          shouldDirty: true,
        },
      );
    }

    onCreate?.(createdUnit);
  }

  function handleCloseModal() {
    handleClose(
      form.getValues().name,
      nameInitialValue,
      onClose,
      form.formState.isDirty,
    );
  }

  function handleName(name: string) {
    setNameInitialValue(name);
  }

  async function saveData(data: Unit) {
    const modifierFunction = item ? handleUpdate : handleCreate;
    await modifierFunction(new Unit(data));

    if (context) {
      setFormItem(data);
    } else {
      onClose();
    }
  }

  useEffect(() => {
    if (inputValue) form.setValue('name', inputValue);
  }, []);

  return (
    <Modal {...rest} isDashboardModal close={handleCloseModal}>
      <FormProvider {...form}>
        <ModalHeader>
          <p className="text--xl__responsive">
            <FormattedMessage
              id={item ? 'buttons.edit-unit' : 'buttons.add-new-unit'}
            />
          </p>
        </ModalHeader>
        <ModalContent>
          <UnitNameInput
            sendName={handleName}
            autoFocus={!isMobile}
            inputSelector="unit-name-input"
          />
        </ModalContent>
        <ModalFooter>
          <div className="btn-group btn-group--simple">
            <Button
              type="button"
              ref={closeButtonRef}
              onClick={onClose}
              size="lrg"
              style="outline"
              btnSelector="modal-cancel-btn"
            >
              <FormattedMessage id="buttons.cancel" />
            </Button>
            <Button
              type="submit"
              onClick={form.handleSubmit(saveData)}
              ref={submitButtonRef}
              size="lrg"
              style="primary"
              btnSelector="save-modal-btn"
            >
              <FormattedMessage id="buttons.save" />
            </Button>
          </div>
        </ModalFooter>
      </FormProvider>
    </Modal>
  );
};
